import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import {
    WorkerCePointRequest,
    WorkerCePointResponse,
    WorkerCePointsClient
} from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';

export interface CePoints extends WorkerCePointResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    cePointsList: CePoints[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    cePointsList: [],
};

const cePointsSlice = createSlice({
    name: 'cePoints',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getCePointsListSuccess: (state, action) => {
            state.cePointsList = action.payload;
            state.isLoading = false;
        },
        addNewCePointsSuccess: (state, action: PayloadAction<CePoints>) => {
            const newItem = action.payload;
            state.cePointsList = [newItem, ...state.cePointsList];
            state.isLoading = false;
        },
        updateCePointsSuccess: (state, action: PayloadAction<CePoints>) => {
            const updatedItem = action.payload;
            const index = state.cePointsList.findIndex(item => item.workerExternalId === updatedItem.workerExternalId);
            state.cePointsList = [...state.cePointsList.slice(0, index), updatedItem, ...state.cePointsList.slice(index + 1)];
            toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
            state.isLoading = false;
        },
        deleteCePointsSuccess: (state, action: PayloadAction<number>) => {
            const deleteId = action.payload;
            state.cePointsList = state.cePointsList.filter(item => item.workerCePointId !== deleteId);
            state.isLoading = false;
        },
    },
});

// Reducer
export default cePointsSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getCePointsListSuccess,
} = cePointsSlice.actions;

// for useSelector
export const selectCePoints = (state: RootState) => state.cePoints as State;

// ----------------------------------------------------------------------
export function getAllCePoints() {
    return async (dispatch, getState) => {
        dispatch(cePointsSlice.actions.startLoading());
        try {
            const client = new WorkerCePointsClient(undefined, axios);
            const response = await client.getAll();
            dispatch(cePointsSlice.actions.getCePointsListSuccess(response));
            return response;
        } catch (error) {
            dispatch(cePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createCePoints(cePoints: CePoints, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(cePointsSlice.actions.startLoading());
        try {
            const request = cePoints as WorkerCePointRequest;
            const client = new WorkerCePointsClient(undefined, axios);
            const response = await client.create(request);
            dispatch(cePointsSlice.actions.addNewCePointsSuccess(response));
            if (showNotification) {
                toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
            }
        } catch (error) {
            dispatch(cePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateCePoints(cePoints: CePoints) {
    return async (dispatch, getState) => {
        dispatch(cePointsSlice.actions.startLoading());
        try {
            const request = cePoints as WorkerCePointRequest;
            const client = new WorkerCePointsClient(undefined, axios);
            const response = await client.edit(cePoints.workerCePointId, request);
            dispatch(cePointsSlice.actions.updateCePointsSuccess(response));
        } catch (error) {
            dispatch(cePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deleteCePoints(cePointsId: number, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(cePointsSlice.actions.startLoading());
        try {
            const client = new WorkerCePointsClient(undefined, axios);
            await client.delete(cePointsId);
            dispatch(cePointsSlice.actions.deleteCePointsSuccess(cePointsId));
            if (showNotification) {
                toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            }
            return true;
        } catch (error) {
            dispatch(cePointsSlice.actions.hasError(true));
            handleError(error);
            return false;
        }
    };
}

export function getAllCePointsByFishingTrip(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(cePointsSlice.actions.startLoading());
        try {
            const client = new WorkerCePointsClient(undefined, axios);
            const response = await client.getByFishingTripNumber(fishingTripNumber);
            dispatch(cePointsSlice.actions.getCePointsListSuccess(response));
            return response;
        } catch (error) {
            dispatch(cePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deleteByFishingTrip(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(cePointsSlice.actions.startLoading());
        try {
            const client = new WorkerCePointsClient(undefined, axios);
            await client.deleteByFishingTrip(fishingTripNumber);
            dispatch(cePointsSlice.actions.getCePointsListSuccess([]));
        } catch (error) {
            dispatch(cePointsSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

/*
This won't work since we do not have tripLengthDays when importing from excel

export function createCePointBatch(fishingTripNumber: string, tripLenthDays: number, cePointsDataModels: CePointsDataModel[]) {
    return async (dispatch, getState) => {
        try {
            const request: WorkerCePointRequest[] = [];
            for (const d of cePointsDataModels) {
                request.push({
                        workerExternalId: d.PGId,
                        workerFullName: d.Name,
                        daysCompleted: d.DaysCompleted,
                        cePoints: d.CePoints,
                        fishingTripNumber: fishingTripNumber,
                        tripLengthDays: tripLenthDays
                } as WorkerCePointRequest);

            const client = new WorkerCePointsClient(undefined, axios);
            await client.createBatch(request);
            }
        } catch (error) {
            handleError(error);
        }
    };
}*/
